import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { runProductionReadinessCheck } from '../../utils/authTest';

interface TestResult {
    jwt: {
        isValid: boolean;
        expiresAt?: Date;
        error?: string;
    };
    sessionTimeout: {
        willExpireIn: number;
        shouldRefresh: boolean;
    };
    apiEndpoints: Array<{
        endpoint: string;
        status: 'success' | 'error';
        responseTime: number;
        error?: string;
    }>;
    environment: string;
    mockDataEnabled: boolean;
}

const ProductionTest: React.FC = () => {
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [isRunning, setIsRunning] = useState(false);

    const runTests = async () => {
        setIsRunning(true);
        try {
            const result = await runProductionReadinessCheck();
            setTestResult(result);
        } catch (error) {
            console.error('Test failed:', error);
        } finally {
            setIsRunning(false);
        }
    };

    useEffect(() => {
        runTests();
    }, []);

    const getStatusIcon = (status: 'success' | 'error' | 'warning') => {
        switch (status) {
            case 'success':
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case 'error':
                return <XCircle className="h-5 w-5 text-red-600" />;
            case 'warning':
                return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
        }
    };

    const getStatusColor = (status: 'success' | 'error' | 'warning') => {
        switch (status) {
            case 'success':
                return 'text-green-600 bg-green-50 border-green-200';
            case 'error':
                return 'text-red-600 bg-red-50 border-red-200';
            case 'warning':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        }
    };

    if (!testResult) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Running production readiness tests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Production Readiness Test</h1>
                    <p className="text-gray-600">Verify that all systems are ready for production</p>
                </div>
                <button
                    onClick={runTests}
                    disabled={isRunning}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                    <RefreshCw className={`h-4 w-4 ${isRunning ? 'animate-spin' : ''}`} />
                    {isRunning ? 'Running...' : 'Run Tests'}
                </button>
            </div>

            {/* Environment Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Environment</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Environment:</span>
                            <span className={`font-medium ${testResult.environment === 'production' ? 'text-green-600' : 'text-yellow-600'}`}>
                                {testResult.environment}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Mock Data:</span>
                            <span className={`font-medium ${testResult.mockDataEnabled ? 'text-yellow-600' : 'text-green-600'}`}>
                                {testResult.mockDataEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">JWT Token</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <div className="flex items-center gap-2">
                                {getStatusIcon(testResult.jwt.isValid ? 'success' : 'error')}
                                <span className={`font-medium ${testResult.jwt.isValid ? 'text-green-600' : 'text-red-600'}`}>
                                    {testResult.jwt.isValid ? 'Valid' : 'Invalid'}
                                </span>
                            </div>
                        </div>
                        {testResult.jwt.expiresAt && (
                            <div className="flex justify-between">
                                <span className="text-gray-600">Expires:</span>
                                <span className="font-medium">
                                    {testResult.jwt.expiresAt.toLocaleString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Session Timeout */}
            <div className="bg-white border rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Session Timeout</h3>
                <div className="flex items-center gap-4">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-600">
                        Token expires in: <span className="font-medium">{testResult.sessionTimeout.willExpireIn} minutes</span>
                    </span>
                    {testResult.sessionTimeout.shouldRefresh && (
                        <span className="text-yellow-600 text-sm">⚠️ Should refresh soon</span>
                    )}
                </div>
            </div>

            {/* API Endpoints */}
            <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">API Endpoints</h3>
                <div className="space-y-3">
                    {testResult.apiEndpoints.map((endpoint, index) => (
                        <div
                            key={index}
                            className={`flex items-center justify-between p-3 rounded-md border ${getStatusColor(endpoint.status)}`}
                        >
                            <div className="flex items-center gap-3">
                                {getStatusIcon(endpoint.status)}
                                <span className="font-mono text-sm">{endpoint.endpoint}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-sm">{endpoint.responseTime}ms</span>
                                {endpoint.error && (
                                    <span className="text-xs opacity-75">{endpoint.error}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                <div className="text-sm text-gray-600 space-y-1">
                    <p>• Environment: {testResult.environment === 'production' ? '✅ Production' : '⚠️ Not production'}</p>
                    <p>• Mock Data: {testResult.mockDataEnabled ? '⚠️ Enabled (should be disabled in production)' : '✅ Disabled'}</p>
                    <p>• JWT Token: {testResult.jwt.isValid ? '✅ Valid' : '❌ Invalid or expired'}</p>
                    <p>• API Endpoints: {testResult.apiEndpoints.filter(e => e.status === 'success').length}/{testResult.apiEndpoints.length} working</p>
                </div>
            </div>
        </div>
    );
};

export default ProductionTest;
